/**
 * One PUT of bytes to a presigned storage URL, with progress. `fetch` cannot report how much of a request body has
 * gone, so this is an `XMLHttpRequest`. Tests give `createBigUploads` another `PutBlob`.
 */

export interface PutRequest {
  readonly url: string;
  readonly body: Blob;
  /** Bytes of the body sent so far. */
  readonly onProgress: (sent: number) => void;
  readonly signal?: AbortSignal | undefined;
}

export interface PutAnswer {
  /** The `ETag` response header, which a multipart upload needs from each part; the storage's CORS rule must expose it. */
  readonly etag: string | undefined;
}

export type PutBlob = (request: PutRequest) => Promise<PutAnswer>;

/** Why a PUT failed: the HTTP status, or 0 when it never got an answer (the network, or an abort). */
export class PutError extends Error {
  readonly status: number;
  readonly aborted: boolean;

  constructor(status: number, message: string, aborted = false) {
    super(message);
    this.name = "PutError";
    this.status = status;
    this.aborted = aborted;
  }
}

const SUCCESS_FROM = 200;
const SUCCESS_BELOW = 300;

export const xhrPut: PutBlob = ({ url, body, onProgress, signal }) =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const stop = () => request.abort();
    request.open("PUT", url);
    request.upload.onprogress = (event) => onProgress(event.loaded);
    request.onload = () => {
      signal?.removeEventListener("abort", stop);
      if (request.status >= SUCCESS_FROM && request.status < SUCCESS_BELOW) {
        onProgress(body.size);
        resolve({ etag: request.getResponseHeader("ETag") ?? undefined });
      } else
        reject(new PutError(request.status, `The storage answered ${String(request.status)}.`));
    };
    request.onerror = () => {
      signal?.removeEventListener("abort", stop);
      reject(new PutError(0, "The storage could not be reached."));
    };
    request.onabort = () => {
      signal?.removeEventListener("abort", stop);
      reject(new PutError(0, "The upload was stopped.", true));
    };
    if (signal?.aborted === true) {
      request.onabort(new ProgressEvent("abort"));
      return;
    }
    signal?.addEventListener("abort", stop, { once: true });
    request.send(body);
  });
